// Material binding + map-set resolution (DECISION-053 Slice-2) — PURE (no THREE / no DOM) so
// the data logic unit-tests headlessly. Turns the boot's material data into a normalized spec
// the composer's async material builder consumes; THREE + KTX2 loading live in the caller.
//
// Data sources (all top-level boot keys, verified live on the Forge canonical boot 2026-07-02):
//   • materialLibrary[materialId]            — the shared map-set (glTF-MR: baseColor/normal +
//                                              a SHARED orm.ktx2 for occlusion(R)+rough(G)+metal(B)),
//                                              factors, tiling.worldSize, exposedParams.tint.
//   • templateSurfaceMaterials[templateId][slot] — the slot→material binding
//                                              ({ materialId, tilingScale } | bare materialId string).
//   • instanceMaterialOverrides[slot]        — per-instance GCG-knob override (null today; hooked).
// templateId resolves from scene.template.id (the scene package) — NOT the shop entry.

/** Resolve the template-default BINDING for a shell surface slot. Per-slot object
 * ({materialId, tilingScale}) or a bare materialId string (whole-surface shorthand). */
export function resolveSlotBinding(templateSurfaceMaterials, templateId, slot) {
	const b = templateSurfaceMaterials?.[templateId]?.[slot];
	if (!b) return null;
	if (typeof b === "string") return { materialId: b, tilingScale: 1, tint: null };
	if (b.materialId) {
		return {
			materialId: b.materialId,
			tilingScale: Number.isFinite(b.tilingScale) ? b.tilingScale : 1,
			tint: b.tint ?? null,
		};
	}
	return null;
}

/** ASK-085 — knob-BINDING from a resolved Material-Instance preset + the knob's tint chip.
 * A Material-Instance preset (DECISION-053: preset = parent material + exposed params; v1 fields)
 * is a templatePresets entry carrying `{ materialId, tilingScale?, tint? }` — the CONTRACT shape
 * Content emits per category. Returns a partial binding to LAYER over the static table via
 * applyInstanceOverride (static → knob → per-instance), or null when there's nothing to layer
 * (legacy procedural preset with no materialId AND no tint chip → static default stands).
 * The knob's tint chip wins over the preset's authored tint; a tint-only result (no materialId)
 * layers onto the static binding's material — the tint-chip rewire. Pure. */
export function resolveKnobBinding(presetEntry, knobTint = null) {
	// Content's live emit (2b71e54) nests the MI fields under `materialInstance` — one namespace
	// shared with the fixture finish's `byKind`. Read nested first; top-level kept for tolerance.
	const src = presetEntry?.materialInstance ?? presetEntry ?? {};
	const materialId = typeof src.materialId === "string" && src.materialId ? src.materialId : null;
	const tint = knobTint ?? src.tint ?? null;
	const tilingScale = Number.isFinite(src.tilingScale) ? src.tilingScale : null;
	if (!materialId && tint == null) return null;
	const out = {};
	if (materialId) out.materialId = materialId;
	if (tint != null) out.tint = tint;
	if (tilingScale != null) out.tilingScale = tilingScale;
	return out;
}

/** Merge a per-instance override (GCG knobs → instanceMaterialOverrides[slot]) over a binding.
 * Precedence: override > template-default binding. No-op when override is null (today's state). */
export function applyInstanceOverride(binding, override) {
	if (!override) return binding;
	const ov = typeof override === "string" ? { materialId: override } : override;
	const base = binding || {};
	const materialId = ov.materialId ?? base.materialId;
	if (!materialId) return binding;
	return {
		materialId,
		tilingScale: Number.isFinite(ov.tilingScale) ? ov.tilingScale : (base.tilingScale ?? 1),
		tint: ov.tint ?? base.tint ?? null,
	};
}

/** Normalize a materialLibrary entry → role→cdnKey map-set + factors + tiling + tint default.
 * DEDUPES the shared ORM (occlusion + metallicRoughness are one `orm.ktx2`). null if missing. */
export function resolveMapSet(materialLibrary, materialId, tier = null) {
	const entry = materialLibrary?.[materialId];
	if (!entry?.maps) return null;
	// ASK-142 §14.2 — the render tier's own map-set when the library publishes one
	// (`mapsByTier.low`: the default with its top mips dropped, ≤ 512 — Content r50); `maps` otherwise,
	// so an entry without tiers is untouched. Per-platform texture LOD, chosen once at the boundary.
	const m = (tier && entry.mapsByTier?.[tier]) || entry.maps;
	const orm = (m.metallicRoughness || m.occlusion)?.cdnKey || null;
	return {
		materialId,
		baseColor: m.baseColor?.cdnKey || null,          // sRGB albedo → material.map
		normal:    m.normal?.cdnKey || null,             // linear      → material.normalMap
		orm,                                             // linear ORM  → roughness/metalness/aoMap
		hasMetalRough: !!m.metallicRoughness?.cdnKey,
		hasAo:         !!m.occlusion?.cdnKey,             // aoMap needs a 2nd UV set (caller dups uv→uv1)
		factors: {
			metalness: Number.isFinite(entry.factors?.metallic)  ? entry.factors.metallic  : 1,
			roughness: Number.isFinite(entry.factors?.roughness) ? entry.factors.roughness : 1,
		},
		worldSize: Number.isFinite(entry.tiling?.worldSize) ? entry.tiling.worldSize : 1,
		tintDefault: Array.isArray(entry.exposedParams?.tint?.default) ? entry.exposedParams.tint.default : [1, 1, 1],
	};
}

/** World metres one texture TILE covers = authored worldSize scaled by the binding's tilingScale
 * (density multiplier — tilingScale 2 → half-size tiles). The UV repeat the projection uses is
 * `worldMetres / metresPerTile`, giving real-world-accurate scale independent of mesh size. */
export function resolveTilingMeters(mapSet, tilingScale) {
	const ws = mapSet?.worldSize > 0 ? mapSet.worldSize : 1;
	const ts = Number.isFinite(tilingScale) && tilingScale > 0 ? tilingScale : 1;
	return ws / ts;
}

/** Effective albedo tint [r,g,b] 0..1 — the binding/override tint if present, else the material's
 * exposedParams default. Accepts "#rrggbb" or [r,g,b]. (glTF: color multiplies baseColorTexture.) */
export function resolveTint(mapSet, binding) {
	const t = binding?.tint;
	if (Array.isArray(t) && t.length >= 3) return [clamp01(t[0]), clamp01(t[1]), clamp01(t[2])];
	if (typeof t === "string") { const rgb = hexToRgb01(t); if (rgb) return rgb; }
	const d = mapSet?.tintDefault;
	return Array.isArray(d) && d.length >= 3 ? [clamp01(d[0]), clamp01(d[1]), clamp01(d[2])] : [1, 1, 1];
}

/** ASK-085 part-2 — "fixture finish" resolution (coord ruling: option b). A fixture-material
 * preset (the FIXTURES dropdown) may carry `materialInstance.byKind = { "<kind>": <binding> }`,
 * where each per-kind value uses the SAME binding vocabulary as templateSurfaceMaterials fixture
 * entries (bare materialId string = whole-fixture, or per-slot object) — so downstream application
 * (resolveFixtureSlotBindings) needs zero new code. A kind present in byKind takes the finish; a
 * kind OMITTED keeps its identity (longbox stays cardboard under a "warm wood" finish) via the
 * static binding. No finish preset / no byKind → static. Pure. */
export function resolveFixtureKindBinding({ staticBinding = null, finishPreset = null, kindFinishPreset = null, kind, instanceCell = null } = {}) {
	// Content's live emit (d548961) puts the matrix at top-level `finishByKind`; the original
	// contract spelling `materialInstance.byKind` stays tolerated. An EXPLICIT null per kind is an
	// honest no-op (`??` falls through), same as an omitted kind. Precedence (Amendment 3):
	//   instance cell > per-KIND default preset cell > global finish preset cell > static.
	const cellOf = (preset) => {
		const matrix = preset?.finishByKind ?? preset?.materialInstance?.byKind ?? null;
		return kind && matrix ? matrix[kind] : null;
	};
	return instanceCell ?? cellOf(kindFinishPreset) ?? cellOf(finishPreset) ?? staticBinding ?? null;
}

/** Amendment 4 — which preset CATEGORY supplies a fixture kind's per-kind vocabulary. Longboxes
 * draw from the box-style family (kraft/white/black — the box's actual finish vocabulary, retiring
 * the honest-null in fixture-material); every other kind uses fixture-material finish presets. Pure. */
export function fixtureKindPresetCategory(kind) {
	return kind === "longbox" ? "box-style" : "fixture-material";
}

/** Amendment 3 — fold the per-KIND default wire grammar `fixtureMaterial.<kind>` (flat 2-segment
 * presetId strings) → { kind: presetId } | null. Guards (contract v3, all no-op): the bare legacy
 * `fixtureMaterial` key (the GLOBAL knob — not part of this fold), unknown kinds (validated
 * against knownKinds when given), non-string values. Pure. */
export function foldKindDefaultKnobs(flatKnobs, knownKinds = null) {
	const known = Array.isArray(knownKinds) && knownKinds.length ? new Set(knownKinds) : null;
	const out = {};
	let n = 0;
	for (const [key, value] of Object.entries(flatKnobs || {})) {
		if (!key.startsWith("fixtureMaterial.")) continue;
		const kind = key.slice("fixtureMaterial.".length);
		if (!kind || kind.includes(".")) continue;
		if (typeof value !== "string" || !value) continue;
		if (known && !known.has(kind)) continue;
		out[kind] = value;
		n++;
	}
	return n ? out : null;
}

/** Amendment 3 — reverse-match an instance cell to the finish preset that produced it, so the
 * panel shows the CHOSEN OPTION NAME (never "(custom override)"). Normalizes both sides through
 * the write macro (bare-string cells expand across materialSlots; array tints → hex). Pure. */
export function matchInstanceCellToPreset({ instanceCell, presets = {}, kind, materialSlots = null } = {}) {
	if (!instanceCell || !kind) return null;
	const A = "X";
	const actual = JSON.stringify(expandInstanceFinishKnobs({ anchorName: A, kind, presetEntry: { finishByKind: { [kind]: instanceCell } }, materialSlots }).writes);
	for (const [presetId, entry] of Object.entries(presets)) {
		const expanded = expandInstanceFinishKnobs({ anchorName: A, kind, presetEntry: entry, materialSlots }).writes;
		if (Object.keys(expanded).length && JSON.stringify(expanded) === actual) return presetId;
	}
	return null;
}

/** ASK-086 — fold the FLAT per-instance wire grammar `material.<anchorName>.<slot>.<prop>`
 * (/knobs/shop primitive caps) into nested per-anchor cells: { [anchor]: { [slot]: {prop: v} } }.
 * Same prefix-fold pattern as MATERIAL_TINT_PAIRS / zoneLight.*; unknown/malformed keys skipped.
 * The nested cell IS the existing per-slot binding vocabulary → feeds resolveFixtureSlotBindings
 * unchanged. Pure. */
export function foldInstanceMaterialKnobs(flatKnobs, placedAnchors = null) {
	// Contract v2 guards (shared fixture): ratified props ONLY (materialId/tint strings,
	// tilingScale number — texture/visible/preset must NOT fold), the signage-reserved `header`
	// slot is denied, keys naming anchors outside `placedAnchors` don't fold (when a set is given;
	// Client passes the composed registry, mint passes post-splice instances), and an empty fold
	// returns NULL — the field is omitted, never {}.
	const ALLOWED_PROPS = { materialId: "string", tint: "string", tilingScale: "number" };
	const placed = Array.isArray(placedAnchors) && placedAnchors.length ? new Set(placedAnchors) : null;
	const out = {};
	let n = 0;
	for (const [key, value] of Object.entries(flatKnobs || {})) {
		if (!key.startsWith("material.") || value == null) continue;
		const parts = key.split(".");
		if (parts.length !== 4) continue;                                     // material.<anchor>.<slot>.<prop>
		const [, anchor, slot, prop] = parts;
		if (!anchor || !slot || !prop) continue;
		if (slot === "header") continue;                                      // signage-reserved
		if (typeof value !== ALLOWED_PROPS[prop]) continue;                   // ratified props only
		if (placed && !placed.has(anchor)) continue;                          // unplaced → no fold
		((out[anchor] ||= {})[slot] ||= {})[prop] = value;
		n++;
	}
	return n ? out : null;
}

/** ASK-086 — the WRITE-TIME macro: expand a finish preset's byKind cell for ONE instance into the
 * flat per-slot knob writes (+ the clear-list for inherit). Returns { clears, writes }:
 *   clears — every existing `material.<anchorName>.` key (always cleared first; inherit = clears only)
 *   writes — flat `material.<anchorName>.<slot>.<prop>` primitives expanded from the cell
 * (bare-string cell → every slot in materialSlots gets that materialId; array tints → hex).
 * A null/absent cell for the kind expands to nothing (honest no-op → inherit). Pure. */
export function expandInstanceFinishKnobs({ anchorName, kind, presetEntry = null, materialSlots = null, existingKeys = [] } = {}) {
	const prefix = `material.${anchorName}.`;
	const clears = (existingKeys || []).filter((k) => typeof k === "string" && k.startsWith(prefix));
	const writes = {};
	const matrix = presetEntry?.finishByKind ?? presetEntry?.materialInstance?.byKind ?? null;
	const cell = kind && matrix ? matrix[kind] : null;
	if (cell) {
		const perSlot = typeof cell === "string"
			? Object.fromEntries(Object.keys(materialSlots || {}).map((s) => [s, { materialId: cell }]))
			: cell;
		for (const [slot, spec] of Object.entries(perSlot || {})) {
			const sp = typeof spec === "string" ? { materialId: spec } : spec;
			if (!sp || typeof sp !== "object") continue;
			if (typeof sp.materialId === "string" && sp.materialId) writes[`${prefix}${slot}.materialId`] = sp.materialId;
			if (Number.isFinite(sp.tilingScale)) writes[`${prefix}${slot}.tilingScale`] = sp.tilingScale;
			const tint = Array.isArray(sp.tint) && sp.tint.length >= 3
				? "#" + sp.tint.slice(0, 3).map((c) => Math.round(Math.max(0, Math.min(1, c)) * 255).toString(16).padStart(2, "0")).join("")
				: (typeof sp.tint === "string" ? sp.tint : null);
			if (tint) writes[`${prefix}${slot}.tint`] = tint;
		}
	}
	return { clears, writes };
}

/** Expand a FIXTURE binding + the catalogue materialSlots → concrete per-slot jobs. The binding
 * is either a bare materialId STRING (whole-fixture shorthand → every slot gets it) or a per-slot
 * OBJECT ({ slot: {materialId,tilingScale,tint} | materialId }). materialSlots maps each slot →
 * mesh identifiers (numeric indices OR material/node-name strings). Pure — the caller resolves the
 * ids to meshes + builds/applies. Returns [{ slot, materialId, tilingScale, tint, ids }]. */
export function resolveFixtureSlotBindings(binding, materialSlots) {
	if (!binding || !materialSlots || typeof materialSlots !== "object") return [];
	const out = [];
	if (typeof binding === "string") {
		for (const [slot, ids] of Object.entries(materialSlots)) {
			if (Array.isArray(ids) && ids.length) out.push({ slot, materialId: binding, tilingScale: 1, tint: null, ids });
		}
	} else if (typeof binding === "object") {
		for (const [slot, spec] of Object.entries(binding)) {
			const ids = materialSlots[slot];
			if (!Array.isArray(ids) || !ids.length) continue;
			const materialId = typeof spec === "string" ? spec : spec?.materialId;
			if (!materialId) continue;
			out.push({
				slot, materialId,
				tilingScale: Number.isFinite(spec?.tilingScale) ? spec.tilingScale : 1,
				tint: (spec && typeof spec === "object") ? (spec.tint ?? null) : null,
				ids,
			});
		}
	}
	return out;
}

/** Header signage source + text SELECTION with the layered precedence (DECISION-053 Slice-2).
 * Pure — the gate against the regression where a shop-level fix drops the per-anchor override.
 *   source: per-anchor headerSource ('label'|'logo')  >  shop signMode (uploaded-logo→logo, else text)
 *   text:   per-anchor label override (anchorLabel ?? extrasLabel)  >  shopName (never-blank fallback)
 * NOTE: text uses the per-anchor OVERRIDE only — NOT the fixture default label (e.g. "Spinner"),
 * which would mask the shop name. The caller passes shopName as the fallback. */
export function resolveHeaderSignageSelection({ headerSource = null, signMode = null, anchorLabel = null, extrasLabel = null, shopName = null } = {}) {
	const source = headerSource === "label" ? "label"
		: headerSource === "logo" ? "logo"
		: (signMode === "uploaded-logo" ? "logo" : "label");
	const text = (anchorLabel ?? extrasLabel) || shopName || null;
	return { source, text };
}

function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function hexToRgb01(hex) {
	const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
	if (!m) return null;
	const n = parseInt(m[1], 16);
	return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
